// Verified resume: prove the re-picked file is byte-identical to the parts
// already landed in S3 before skipping them. Part ETags are plain MD5s of the
// part bytes (SSE-S3), so hashing each landed range locally settles it —
// any mismatch means a different file (a re-export), never spliced.

import SparkMD5 from 'spark-md5'
import type { VodUploadedPart } from '../types/vod'

// Read parts through 8 MB slices so memory holds one slice, never a 100 MB part
const HASH_SLICE_BYTES = 8 * 1024 * 1024

export const md5OfFileRange = async (
  file: File,
  start: number,
  end: number
): Promise<string> => {
  const spark = new SparkMD5.ArrayBuffer()
  for (let offset = start; offset < end; offset += HASH_SLICE_BYTES) {
    const sliceEnd = Math.min(end, offset + HASH_SLICE_BYTES)
    const buffer = await file.slice(offset, sliceEnd).arrayBuffer()
    spark.append(buffer)
  }
  return spark.end()
}

export interface PartVerificationResult {
  ok: boolean
  /** First part number whose local hash diverged from S3's ETag */
  mismatchedPart?: number
}

/**
 * Hash each already-landed part range of the re-picked file (boundaries from
 * the session's part_size) and compare against S3's part ETags.
 */
export const verifyUploadedParts = async (
  file: File,
  partSize: number,
  uploadedParts: VodUploadedPart[],
  onProgress?: (verified: number, total: number) => void
): Promise<PartVerificationResult> => {
  const total = uploadedParts.length
  let verified = 0

  for (const part of uploadedParts) {
    const start = (part.part_number - 1) * partSize
    const end = Math.min(file.size, start + partSize)
    const digest = await md5OfFileRange(file, start, end)
    const etag = part.etag.replace(/"/g, '').toLowerCase()
    if (digest !== etag) {
      return { ok: false, mismatchedPart: part.part_number }
    }
    verified += 1
    onProgress?.(verified, total)
  }

  return { ok: true }
}
